<template>
	<v-progress-circular
		class="d-flex justify-center"
		v-if="fields_loading"
		:size="20"
		:width="3"
	/>

	<v-form
		v-else
		class="registration-form-step2"
		ref="form"
		v-model="valid"
		lazy-validation
		@submit.prevent="validateAndSubmit"
	>
		<div
			class="registration-form-step2__section"
			v-for="(fieldsGroup, sectionName) in groupedFields"
			:key="sectionName"
		>
			<h3 class="registration-form-step2__section-title">
				{{ sectionName }}
			</h3>

			<v-row>
				<v-col
					v-for="(fieldItem, index) in fieldsGroup"
					:key="fieldItem.field"
					cols="12"
					class="py-0"
					:cols="getColsForIndex(index, fieldsGroup.length)"
				>
					<!-- user_logo => v-file-input -->
					<div v-if="fieldItem.type === 'user_logo'">
						<v-file-input
							:class="{ 'field-required': fieldItem.required }"
							:value="getFieldValue(fieldItem)"
							@change="setFieldValue(fieldItem, $event)"
							:error-messages="field_errors[fieldItem.field]"
							:rules="getFieldRules(fieldItem)"
							:placeholder="fieldItem.title"
							:required="!!fieldItem.required"
							color="red"
							class="pt-0 field"
							accept="image/*"
							truncate-length="40"
						/>
					</div>

					<!-- html => v-textarea -->
					<div v-else-if="fieldItem.type === 'html'">
						<v-textarea
							:class="{ 'field-required': fieldItem.required }"
							:value="getFieldValue(fieldItem)"
							@input="setFieldValue(fieldItem, $event)"
							:error-messages="field_errors[fieldItem.field]"
							:rules="getFieldRules(fieldItem)"
							:placeholder="fieldItem.title"
							:required="!!fieldItem.required"
							color="red"
							class="pt-0 field"
							auto-grow
						/>
					</div>

					<!-- list => v-text-field (только цифры) -->
					<div v-else-if="fieldItem.type === 'list'">
						<v-text-field
							:class="{ 'field-required': fieldItem.required }"
							:value="getFieldValue(fieldItem)"
							@input="setFieldValue(fieldItem, $event)"
							:error-messages="field_errors[fieldItem.field]"
							:rules="getFieldRules(fieldItem)"
							:placeholder="fieldItem.title"
							:required="!!fieldItem.required"
							color="red"
							class="pt-0 field"
							type="number"
						/>
					</div>

					<!-- phone => v-text-field -->
					<div v-else-if="fieldItem.type === 'phone'">
						<v-text-field
							:class="{ 'field-required': fieldItem.required }"
							:value="getFieldValue(fieldItem)"
							@input="setFieldValue(fieldItem, $event)"
							:error-messages="field_errors[fieldItem.field]"
							:rules="getFieldRules(fieldItem)"
							:placeholder="fieldItem.title"
							:required="!!fieldItem.required"
							color="red"
							class="pt-0 field"
							type="tel"
						/>
					</div>

					<!-- email => v-text-field (type="email") -->
					<div v-else-if="fieldItem.type === 'email'">
						<v-text-field
							:class="{ 'field-required': fieldItem.required }"
							:value="getFieldValue(fieldItem)"
							@input="setFieldValue(fieldItem, $event)"
							:error-messages="field_errors[fieldItem.field]"
							:rules="getFieldRules(fieldItem)"
							:placeholder="fieldItem.title"
							:required="!!fieldItem.required"
							color="red"
							class="pt-0 field"
							type="email"
						/>
					</div>

					<!-- По умолчанию: v-text-field -->
					<div v-else>
						<v-text-field
							:class="{ 'field-required': fieldItem.required }"
							:value="getFieldValue(fieldItem)"
							@input="setFieldValue(fieldItem, $event)"
							:error-messages="field_errors[fieldItem.field]"
							:rules="getFieldRules(fieldItem)"
							:placeholder="fieldItem.title"
							:required="!!fieldItem.required"
							color="red"
							class="pt-0 field"
							:type="fieldItem.type === 'url' ? 'url' : 'text'"
						/>
					</div>
				</v-col>
			</v-row>
		</div>
		<v-btn
			v-if="registration_fields.length > 0"
			class="registration-form-step2__submit elevation-0"
			type="submit"
			:loading="isLoading"
		>
			{{ signUpButtonTitle }}
		</v-btn>
	</v-form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Form from '@/mixins/Form'
import { ruleRequired } from '@/utils/rules'

export default {
	mixins: [Form],
	props: {
		initFields: {
			type: Object,
			default: () => ({}),
		},
		categorySettings: {
			type: Object,
			default: null,
		}
	},

	data() {
		return {
			defaultFields:[
				{ field: "user_fname",
					required: 1,
					section: "Profile Info",
					title: "First Name",
					type: "string",
					value: null
				},
				{
					field: "user_lname",
					required: 1,
					section: "Profile Info",
					title: "Last Name",
					type: "string",
					value: null
				}
			],
			fields: {
				address: {
					Home: {},
					Work: {},
				},
				user_logo: [],
			},
			valid: false,
			field_errors: {},
		}
	},

	computed: {
		...mapGetters('settings', [
			'registration_fields',
			'fields_loading',
		]),
		...mapGetters('auth', ['isLoading']),
		isUserSlug() {
			const urlParams = new URLSearchParams(window.location.search);
			return urlParams.get('slug') === 'user';
		},

		signUpButtonTitle() {
			if (this.categorySettings) {
				return this.categorySettings?.signup_button  || 'Sign Up'
			}
			return null
		},
		groupedFields() {
			if (!this.isUserSlug){
				return this.defaultFields.reduce((acc, item) => {
					const section = item.section || 'General'
					if (!acc[section]) {
						acc[section] = []
					}
					acc[section].push(item)
					return acc
				}, {})
			} else {
				return this.registration_fields.reduce((acc, item) => {
					const section = item.section || 'General'
					if (!acc[section]) {
						acc[section] = []
					}
					acc[section].push(item)
					return acc
				}, {})
			}
		},
	},

	methods: {
		...mapActions('auth', ['signUp']),
		...mapActions('settings', ['getRegistrationFields']),

		getColsForIndex(index, totalCount) {
			const isLast = index === totalCount - 1
			const isOdd = totalCount % 2 !== 0

			if (isOdd && isLast) {
				return 12
			} else {
				return 6
			}
		},
		getFieldRules(fieldItem) {
			const rules = []
			if (fieldItem.required) {
				rules.push(v => ruleRequired(v) || 'This field is required.')
			}
			if (fieldItem.type === 'list') {
				rules.push(v => {
					if (!fieldItem.required && (!v || v === '(None selected)')) {
						return true
					}
					return /^\d+$/.test(v) || 'Only numbers can be entered.'
				})
			}

			return rules
		},
		getFieldValue(fieldItem) {
			if (fieldItem.section === 'Home Address') {
				return this.fields.address.Home[fieldItem.field] || ''
			} else if (fieldItem.section === 'Work Address') {
				return this.fields.address.Work[fieldItem.field] || ''
			} else {
				return this.fields[fieldItem.field] || ''
			}
		},
		setFieldValue(fieldItem, value) {
			if (fieldItem.section === 'Home Address') {
				this.$set(this.fields.address.Home, fieldItem.field, value)
			} else if (fieldItem.section === 'Work Address') {
				this.$set(this.fields.address.Work, fieldItem.field, value)
			} else {
				this.$set(this.fields, fieldItem.field, value)
			}
		},
		async validateAndSubmit() {
			const isFormValid = await this.$refs.form.validate()
			if (isFormValid) {
				const result = await this.signUp(this.fields)
				if (result) {
					this.$router.push({ name: 'login' })
				} else {
					this.$emit('stepBack')
				}
			}
		},
	},

	created() {
		this.getRegistrationFields(this.$route.query.user_category_id)
		Object.keys(this.initFields).forEach((key) => {
			if (key.startsWith('second_address_')) {
				this.$set(this.fields.address.Home, key, this.initFields[key])
			} else if (key.startsWith('first_address_')) {
				this.$set(this.fields.address.Work, key, this.initFields[key])
			} else {
				this.$set(this.fields, key, this.initFields[key])
			}
		})
		this.registration_fields.forEach((f) => {
			if (f.section === 'Home Address') {
				if (this.fields.address.Home[f.field] === undefined) {
					this.$set(this.fields.address.Home, f.field, f.value || '')
				}
				this.$set(this.field_errors, f.field, null)
			} else if (f.section === 'Work Address') {
				if (this.fields.address.Work[f.field] === undefined) {
					this.$set(this.fields.address.Work, f.field, f.value || '')
				}
				this.$set(this.field_errors, f.field, null)
			} else {
				if (this.fields[f.field] === undefined) {
					this.$set(
						this.fields,
						f.field,
						f.type === 'user_logo' ? [] : (f.value || ''),
					)
				}
				this.$set(this.field_errors, f.field, null)
			}
		})
	},
}
</script>

<style lang="scss">
.registration-form-step2 {
	&__section {
		margin-top: 1rem;

		&-title {
			font-size: 18px;
			font-weight: 700;
			color: #4a4a4a;
			margin-bottom: 12px;
		}
	}

	&__submit {
		width: 100%;
		height: 50px !important;
		border-radius: 8px;
		font-size: 18px;
		font-weight: 700;
		color: #fff !important;
		text-transform: capitalize;
		background-color: var(--button-main-background-color) !important;
		margin-top: 32px;
	}

	.field-required::after {
		content: ' *';
		color: red;
	}

	.field {
		input {
			font-size: 13px;
			font-weight: 500;
			max-height: 50px;
			padding-left: 10px;
		}

		.v-input__slot {
			border-bottom: 1px solid rgba(0, 0, 0, 0.6);
		}
	}

	input::placeholder {
		color: #9b9b9b !important;
		opacity: 1;
	}
}
</style>
